import React, { useState, useCallback } from 'react';
import { Platform, View, Text, TextInput, Image, StyleSheet, Alert, TouchableOpacity, ScrollView, SafeAreaView } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { fetchAuthSession } from 'aws-amplify/auth';
import { RootStackParamList } from './App.web';
import { apiUrl } from './utils/config';

const CreateStoryPage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageKey, setImageUrl] = useState('');
  const [firstPrompt, setFirstPrompt] = useState('');
  const [objective, setObjective] = useState('');
  const [chapters, setChapters] = useState('0');
  const [category, setCategory] = useState('Mythical');
  const [tags, setTags] = useState<string[]>([]);
  const [items, setItems] = useState<string[]>([]);
  const [newItem, setNewItem] = useState('');
  const [error, setError] = useState<string | null>(null);

  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const handleSubmit = async () => {
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');

      const response = await fetch(`${apiUrl}stories/add-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          title,
          chapters: chapters,
          description,
          image_key: imageKey,
          first_prompt: firstPrompt,
          objective,
          category,
          tags,
          items
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      navigation.navigate('StoriesPage');
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Error creating story:', error);
        setError(error.message);
      }
    }
  };

  const handleTagChange = (tag: string) => {
    setTags((prevTags) => prevTags.includes(tag) ? prevTags.filter(t => t !== tag) : [...prevTags, tag]);
  };

  const handleAddItem = () => {
    if (newItem.trim()) {
      setItems([...items, newItem.trim()]);
      setNewItem('');
    }
  };

  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('StoriesPage'); // Navigate to StoriesPage explicitly
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => handleBack()}>
            <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
          </TouchableOpacity>
        </View>
        <View style={styles.titleContainer}>
          <Text style={styles.heading}>Create a New Story</Text>
          {error && <Text style={styles.error}>{error}</Text>}
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Title:</Text>
          <TextInput style={styles.input} value={title} onChangeText={setTitle} />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Chapters:</Text>
          <TextInput style={styles.input} keyboardType="numeric" value={chapters} onChangeText={setChapters} />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Description:</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={description}
            onChangeText={setDescription}
            multiline
          />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Image Key:</Text>
          <TextInput style={styles.input} value={imageKey} onChangeText={setImageUrl} />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>First Prompt:</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={firstPrompt}
            onChangeText={setFirstPrompt}
            multiline
          />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Objective:</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={objective}
            onChangeText={setObjective}
            multiline
          />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Category:</Text>
          <Picker selectedValue={category} onValueChange={setCategory} style={styles.input}>
            <Picker.Item label="Mythical" value="Mythical" />
            <Picker.Item label="Medieval" value="Medieval" />
            <Picker.Item label="Whimsical" value="Whimsical" />
            <Picker.Item label="Adventure" value="Adventure" />
          </Picker>
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Tags:</Text>
          {['Popular', 'New', 'Seasonal'].map(tag => (
            <TouchableOpacity key={tag} style={styles.tagButton} onPress={() => handleTagChange(tag)}>
              <Text style={[styles.tagText, tags.includes(tag) && styles.selectedTag]}>{tag}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Add Item:</Text>
          <TextInput style={styles.input} value={newItem} onChangeText={setNewItem} />
          <TouchableOpacity style={styles.addButton} onPress={handleAddItem}>
            <Text style={styles.addButtonText}>Add</Text>
          </TouchableOpacity>
        </View>
        {items.map((item, index) => (
          <Text key={index} style={styles.item}>{item}</Text>
        ))}
        <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
          <Text style={styles.submitButtonText}>Create Story</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    backgroundColor: '#f9f9f9',
    flexGrow: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    ...Platform.select({
      web: {
        padding: 15,
      },
    }),
  },
  backIcon: {
    width: 24,
    height: 24,
  },
  titleContainer: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 800,
  },
  heading: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'left',
  },
  error: {
    color: 'red',
    marginBottom: 10,
  },
  formGroup: {
    marginBottom: 15,
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 5,
  },
  input: {
    padding: 10,
    borderColor: '#ccc',
    borderWidth: 1,
    borderRadius: 5,
    backgroundColor: 'white',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  tagButton: {
    padding: 10,
    marginVertical: 5,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#ccc',
  },
  tagText: {
    fontSize: 16,
  },
  selectedTag: {
    color: 'blue',
    fontWeight: 'bold',
  },
  addButton: {
    backgroundColor: '#4CAF50',
    padding: 10,
    marginTop: 10,
    alignItems: 'center',
    borderRadius: 5,
  },
  addButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  item: {
    fontSize: 16,
    marginBottom: 5,
  },
  submitButton: {
    backgroundColor: '#4CAF50',
    padding: 15,
    borderRadius: 5,
    alignSelf: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 800,
    marginVertical: 20,
  },
  submitButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
});

export default CreateStoryPage;
